"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const config_1 = require("./config");
const core_routes_1 = require("./core-routes");
const auth_routes_1 = require("./auth-routes");
const compliance_routes_1 = require("./compliance-routes");
/**
 * ChipperSDK API wrapper.
 * Provides helper methods for calling the Chipper Core API.
 *
 * @class ChipperSDK
 */
class ChipperSDK {
    /**
     * Creates an instance of ChipperSDK.
     * @param {string} [baseUrl='http://localhost:3002/v1/'] Base API URL.
     * @param {string} firebaseToken The firebase auth token for the signed-in user
     * @param {object} [axiosOptions={}] The default Axios options to use with each request
     * @memberof ChipperSDK
     */
    constructor(firebaseToken, baseUrl = 'http://localhost:3002/v1/', axiosOptions = {}) {
        config_1.configInstance.setBaseUrl(baseUrl);
        config_1.configInstance.setAuthToken(firebaseToken);
        config_1.configInstance.setOptions(axiosOptions);
        this.config = config_1.configInstance;
        this.routes = new core_routes_1.CoreRoutes();
        this.authRoutes = new auth_routes_1.AuthRoutes();
        this.complianceRoutes = new compliance_routes_1.ComplianceRoutes();
        this.addJwtRefreshInterceptor();
    }
    // Set a callback for when the JWT refresh fails
    onJwtRefreshFailed(fn) {
        this.jwtRefreshFailedCallback = fn;
    }
    // Set a callback for when the JWT is refreshed
    onJwtRefresh(fn) {
        this.jwtRefreshCallback = fn;
    }
    // Attach an interceptor to automatically attempt a JWT refresh if expired.
    addJwtRefreshInterceptor() {
        this.requestingJwtRefresh = false;
        this.config.axiosInstance.interceptors.response.use((response) => {
            // Any status code that lie within the range of 2xx cause this function to trigger
            return response;
        }, async (error) => {
            // Any status codes that fall outside the range of 2xx cause this function to trigger
            if (error.config &&
                error.response &&
                error.response.status === 401 &&
                error.response.data.error.message === 'Token Expired' &&
                !this.requestingJwtRefresh) { // Prevent multiple concurrent JWT refresh attempts
                return this.attemptJwtRefresh(error);
            }
            // If not a "Token Expired" error, just percolate the error up the stack
            return Promise.reject(error);
        });
    }
    // Attempt to refresh the token, and retry the request after.
    async attemptJwtRefresh(error) {
        // Check if auth token has changed since request was sent
        const requestToken = error.config.headers.Authorization;
        if (requestToken !== this.config.authToken) {
            // Retry the request with the new auth token
            return this.retryRequest(error);
        }
        // If the token hasn't changed, try requesting a new one
        try {
            this.requestingJwtRefresh = true;
            const response = await this.authRoutes.jwt.refreshJwt();
            this.config.setAuthToken(response.data.token);
            // Notify the host application that the token has been refreshed.
            // This should trigger a callback in order to save the new token to local storage.
            if (this.jwtRefreshCallback && typeof this.jwtRefreshCallback === 'function') {
                this.jwtRefreshCallback(this.config.authToken);
            }
            this.requestingJwtRefresh = false;
            // Retry the request with the new auth token
            return this.retryRequest(error);
        }
        catch (err) {
            this.requestingJwtRefresh = false;
            // The refresh failed - notify the host application via a callback
            // In this scenario, the best option is probably to send the user back to the login page.
            if (this.jwtRefreshFailedCallback && typeof this.jwtRefreshFailedCallback === 'function') {
                this.jwtRefreshFailedCallback();
            }
            return Promise.reject(error);
        }
    }
    // Retry a failed request
    retryRequest(error) {
        const requestRetry = error.config;
        // Update the auth token with the current token
        requestRetry.headers.Authorization = this.config.authToken;
        // Re-trigger the request
        return this.config.axiosInstance.request(requestRetry);
    }
}
exports.default = ChipperSDK;
//# sourceMappingURL=index.js.map